import "dayjs/locale/en-gb";

import { Avatar, Button, Grid, ListItemText, TextField, Tooltip } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useContext } from "react";

import { useMutation } from "@apollo/client/react";
import { ReportContext } from "@/Contexts/ReportContext";
import PermissionContext from "@/Contexts/Permissions";
import { SubmitReportDocument, PermissionActionEnum, PermissionEntityEnum } from "@/graphql/generated/graphql";
import { arrayLength } from "@/utils/helperFunctions";
import { displayNotification } from "@/utils/notifications";
import ReportDiary from "./DiaryEntry";
import WeatherCard from "./WeatherCard";
import { StyledAutocomplete, StyledDatePicker } from "@/theme/styled-components";
import RequestPermissionTooltip from "../RequestPermissionTooltip";
dayjs.extend(utc);

export default function HeaderSection() {
  const { projects, selectedProject, selectedReport, onUpdate, isWeekendsEnabled } = useContext(ReportContext);
  const { can, checkRequestExists, requestPermission } = useContext(PermissionContext);

  const handleDateSelection = (selectedDate: dayjs.Dayjs | null) => {
    onUpdate(selectedDate?.format("DD/MM/YYYY"), "date");
  };

  const [submitReport] = useMutation(SubmitReportDocument);

  const handleSubmit = () => {
    submitReport({
      variables: { reportId: selectedReport?.id as string },
      onCompleted: () => {
        displayNotification({ type: "success", text: "Report submitted successfully" })
        onUpdate(dayjs().add(1, 'day').format("DD/MM/YYYY"), "date")
      },
      onError: () => displayNotification({ type: "error", text: "Failed to submit report" }),
    });
  };

  const disabledSubmit = !can(PermissionActionEnum.Create, PermissionEntityEnum.Reports, { projectId: selectedProject?.id }) ||
    selectedReport?.stage !== "open";

  if (!projects || !selectedProject) return <></>;

  return (
    <Grid
      container
      marginTop={1}
      alignItems={'center'}
      data-testid="report-header"
      paddingX={2}
      size={12}
      spacing={1}
    >
      <StyledAutocomplete
        key={`project-code-${selectedProject.id}`}
        id="project-code-selector"
        disablePortal
        defaultValue={selectedProject}
        onChange={(_, newValue) => onUpdate(newValue, "project")}
        options={projects}
        disableClearable
        // @ts-expect-error everything is fine, typescript being annoying because of the use of styled
        getOptionLabel={(option) => option.code}
        renderInput={(params) => <TextField {...params} label="Project Code" margin="dense" size="small" />}
      />
      <StyledAutocomplete
        key={`project-name-${selectedProject.id}`}
        id="project-title-selector"
        disablePortal
        defaultValue={selectedProject}
        onChange={(_, newValue) => onUpdate(newValue, "project")}
        options={projects}
        disableClearable
        // @ts-expect-error everything is fine, typescript being annoying because of the use of styled
        getOptionLabel={(option) => option.title}
        width={250}
        renderInput={(params) => <TextField {...params} label="Project Name" margin="dense" size="small" />}
      />
      <StyledAutocomplete
        disablePortal
        defaultValue={selectedProject.managers?.[0] || null}
        options={selectedProject.managers || []}
        disableClearable
        getOptionLabel={() => `+${arrayLength(selectedProject.managers)} People Associated`}
        width={200}
        fullWidth
        renderInput={(params) => <TextField {...params} label="Manager" margin="dense" size="small" disabled />}
        renderOption={(props, option) => (
          // @ts-expect-error everything is fine, typescript being annoying because of the use of styled
          (<li {...props} key={`manager-${option.id}-${option.companyResource.name}`}>
            {/* @ts-expect-error everything is fine, typescript being annoying because of the use of styled */}
            <Avatar src={option.avatarUrl} sx={{ width: 24, height: 24 }} />
            {/* @ts-expect-error everything is fine, typescript being annoying because of the use of styled */}
            <ListItemText primary={option.companyResource.name} sx={{ marginLeft: 1, '&>*': { fontSize: '0.75rem !important' } }} />
          </li>)
        )}
      />
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
        <StyledDatePicker
          key={`report-date-${selectedReport?.id}`}
          label="Report Date"
          defaultValue={dayjs(selectedReport?.reportDate)}
          onChange={(newValue) => handleDateSelection(newValue as Dayjs | null)}
          fontSize='0.75rem'
          slotProps={{
            textField: { size: "small", margin: "dense" }
          }}
          shouldDisableDate={(date) => {
            // If weekends are enabled, disable Sat (6) and Sun (0)
            if (isWeekendsEnabled) {
              const day = (date as Dayjs).day();
              return day === 0 || day === 6;
            }
            return false;
          }}
        />
      </LocalizationProvider>
      <WeatherCard />
      <Grid
        container
        justifyContent="flex-end"
        alignItems="center"
        marginRight={2}
        size="grow"
      >
        <ReportDiary />

        <RequestPermissionTooltip
          hasPermission={can(PermissionActionEnum.Create, PermissionEntityEnum.Reports, { projectId: selectedProject?.id })}
          requiredPermission={{
            action: PermissionActionEnum.Create,
            entity: PermissionEntityEnum.Reports
          }}
          checkRequestExists={checkRequestExists}
          onRequest={requestPermission}
        >
          <span>
            <Tooltip title="Submit Report">
              <Button
                variant="outlined"
                onClick={handleSubmit}
                color="success"
                size='small'
                disabled={disabledSubmit}
                sx={{ ml: 0.5 }}
              >
                Submit
              </Button>
            </Tooltip>
          </span>
        </RequestPermissionTooltip>
      </Grid>
    </Grid>
  );
}
